import AddIcon from "@mui/icons-material/Add";
import { Alert, IconButton, TextField } from "@mui/material";
import * as React from "react";

import { useAnalytics } from "@/providers/analytics";
import { CollectionRecord } from "@/shared/domain/collections";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { Tag } from "@/shared/foundation/primitives";

import styles from "./CollectionDetailsTab.module.css";
import { EditableCollectionMetadata } from "./EditableCollectionMetadata.tsx";

interface CollectionDetailsTabProps {
  collection: CollectionRecord;
}

export const CollectionDetailsTab: React.FC<CollectionDetailsTabProps> = ({
  collection,
}) => {
  const { trackEvent } = useAnalytics();
  const [tagInput, setTagInput] = React.useState("");

  const { mutateAsync: updateCollection, error } = useUpdateCollection();

  async function handleAddTag() {
    const tag = tagInput.trim();
    if (!tag) {
      return;
    }
    trackEvent("AddCollectionTagClicked", {
      collectionId: collection.collection_id,
    });
    await updateCollection({
      collectionId: collection.collection_id,
      resourceOwnerId: collection.org_id,
      updates: { add_tags: [tag] },
    });
    setTagInput("");
  }

  async function handleRemoveTag(tag: string) {
    await updateCollection({
      collectionId: collection.collection_id,
      resourceOwnerId: collection.org_id,
      updates: { remove_tags: [tag] },
    });
  }

  return (
    <div className={styles.detailsGrid}>
      {error && (
        <Alert severity="error" style={{ gridColumn: "1 / -1" }}>
          {error.message}
        </Alert>
      )}

      <span className={styles.detailLabel}>Name / Description</span>
      <EditableCollectionMetadata
        name={collection.name ?? ""}
        description={collection.description ?? ""}
        collectionId={collection.collection_id}
        orgId={collection.org_id}
      />

      <span className={styles.detailLabel}>Created</span>
      <span className={styles.detailValue}>
        {new Date(collection.created).toLocaleString()}
      </span>

      <span className={styles.detailLabel}>Modified</span>
      <span className={styles.detailValue}>
        {new Date(collection.updated).toLocaleString()} by{" "}
        <a
          href={`mailto:${collection.updated_by}`}
          className={styles.detailLink}
        >
          {collection.updated_by}
        </a>
      </span>

      <span className={styles.detailLabel}>Tags</span>
      <div className={styles.tagSection}>
        <div className={styles.tagInput}>
          <TextField
            size="small"
            placeholder="Tag"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void handleAddTag();
              }
            }}
            style={{ width: 140 }}
            inputProps={{ style: { fontSize: "0.875rem" } }}
          />
          <IconButton
            size="small"
            onClick={() => void handleAddTag()}
            disabled={!tagInput.trim()}
            aria-label="Add tag"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </div>
        {collection.tags.length > 0 && (
          <div className={styles.tagChips}>
            {collection.tags.map((tag) => (
              <Tag
                key={tag}
                label={tag}
                icon={null}
                onDelete={() => void handleRemoveTag(tag)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
